import CssBaseline from "@mui/material/CssBaseline";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import PatientSearchBar from "./PatientSearchBar";
import LocationSelector from "./LocationSelector";
import UserProfileMenu from "./UserProfileMenu";
import SideDrawer from "./SideDrawer";
import MenuButton from "./MenuButton";
import useStore from "../../helpers/useStore";
import { AuthContext } from "../../stores/Auth/auth.provider";

const drawerWidth = 180;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  paddingBottom: 5,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const AppBarOverView = () => {
  const [open, setOpen] = useState(false);
  const AuthStore = useStore(AuthContext);
  const isAuthenticated = !!AuthStore.authToken;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {isAuthenticated && <MenuButton onOpen={handleDrawerOpen} isOpen={open} />}

          {/* Use the Link component to wrap the logo and make it clickable */}
          <Link
            to="/dashboard"
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
            }}
          >
            {((): boolean => {
              const today = new Date();
              return today.getMonth() === 1 && today.getDate() === 14;
            })() ? (
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/49/3D_heart.png"
                alt="Happy Valentines Day"
                height="58"
              />
            ) : (
              <img src="favicon.png" alt="Logo" height="58" />
            )}
            <Typography
              variant="h4"
              noWrap
              sx={{ display: { xs: "none", sm: "block" }, color: "white" }}
            >
              {((): boolean => {
                const today = new Date();
                return today.getMonth() === 3 && today.getDate() === 1;
              })()
                ? "THEORG"
                : "Mobili Decision"}
            </Typography>
            {process.env.REACT_APP_INSTANCE_NAME &&
              process.env.REACT_APP_INSTANCE_NAME != "production" && (
                <Typography
                  variant="h4"
                  noWrap
                  sx={{
                    display: { xs: "none", sm: "block" },
                    color: "info.main", // Use the info color from the theme
                    fontStyle: "italic", // Make the text italic
                    px: 2, // Add padding (shorthand for padding in all directions)
                  }}
                >
                  {process.env.REACT_APP_INSTANCE_NAME}
                  {process.env.REACT_APP_INSTANCE_VERSION &&
                    ` ${process.env.REACT_APP_INSTANCE_VERSION}`}
                </Typography>
              )}
          </Link>

          <Box sx={{ flexGrow: 1 }} />
          {isAuthenticated && (
            <>
              <PatientSearchBar />
              <LocationSelector />
              <UserProfileMenu />
            </>
          )}
        </Toolbar>
      </AppBar>
      {isAuthenticated && <SideDrawer isOpen={open} onClose={handleDrawerClose} />}
    </>
  );
};

export default AppBarOverView;
